import {
  Component, OnInit, Input, Output,
  Renderer, EventEmitter, ElementRef, ViewChild, HostBinding, ContentChild,
  ContentChildren, QueryList
} from '@angular/core';
import { MdMenuTrigger } from '@angular/material';
import { CfCoreComponent } from '../core/core.component';
import { CfMenuItemComponent } from '../menu-item/menu-item.component';
import { IconModel } from '../../models/icon/icon.model';
import { MenuModel } from '../../models/menu/menu.model';
import { MenuStylingModel } from '../../models/menu/menu-styling.model';
import { MenuItemModel } from '../../models/menu/menu-item.model';
import { MenuItemStylingModel } from '../../models/menu/menu-item-styling.model';
import { MenuTemplates } from '../../templates/menu.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CF Menu Component</p>
 * <pre>
 * <code>
 * import {{'{'}} MenuModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} MenuStylingModel {{'}'}} from 'cedrus-fusion'
 * <</code>cf-menu<code>></code><code><</code><code>/</code>cf-menu<code>>
 * </code>
 * </pre>
 */
export class CfMenuComponent extends CfCoreComponent implements OnInit {
/**
 * @hidden
 */
notificationClickable = '';
/**
 * <pre>{{'{'}}
 *  display: boolean,							// True or False <b>Default: True</b>
 *  disable: boolean,							// True or False <b>Default: False</b>
 *  triggerIcon: IconModel,				// Refer to icon component
 *  menuItems: any,								// Array of menu items with type any so each item can
 *                                  have any structure together with MenuItemModel properties
 *  itemsSource: string,				  // Type of items source. For current time exists only: <b>fromModel</b>
 * {{'}'}}
 * </pre>
 * <p>Example</p>
 * <pre>{{'{'}}
 * 	display: true,
 * 	disable: false,
 * 	itemsSource: 'fromModel',
 *  menuItems:[
 *   {{'{'}}
 *    buttonProperty:{{'{'}}
 *     label: "USD account",
 *     iconProperty:{{'{'}}
 *      name: 'fa-usd',
 *      size: '24px'
 *     {{'}'}},
 *     iconPosition:"left"
 *    {{'}'}}
 *   {{'}'}},
 *   {{'{'}}
 *    buttonProperty:{{'{'}}
 *     label: "EURO account",
 *     iconProperty:{{'{'}}
 *      name: 'fa-eur',
 *      size: '24px'
 *     {{'}'}},
 *     iconPosition:"left"
 *    {{'}'}}
 * 	 {{'}'}}
 *  ]
 *  triggerIcon:{{'{'}}
 *   name: "account_balance",
 *   size: "30px"
 *  {{'}'}}
 * {{'}'}}
 * </pre>
 */
public properties: MenuModel;
/**
 * <p> dynamicClass: function() -> string,	// Function that returns name of the class </p>
 * <p> class: string												// Name of the css class selector </p>
 * <p> themeColor: string										// primary/accent/warn </p>
 * <pre>
 * {{'{'}}
 * 	// Container surrounding the Menu
 * 	container:{{'{'}}
 *   dynamicClass,
 *   class
 * 	{{'}'}},
 * 	// Check Icon Component
 * 	iconStyling:IconStylingModel,
 * 	// Toggle Icon styling with same structure as above
 * 	menuItemStyling:{{'{'}}
 *   container: {{'{'}}{{'}'}},
 *   //Check Button Component
 *   buttonStyling:ButtonStylingModel
 * {{'}'}}
 * {{'}'}}
 * </pre>
 */
public styling: MenuStylingModel;
/**
 * <p>Icon name of menu trigger</p>
 * @param {?} value
 * @return {?}
 */
set iconName(value: string) {
    if (this.properties.triggerIcon == null) { this.properties.triggerIcon = new IconModel(); }
    this.properties.triggerIcon.name = value;
    this.properties.triggerIcon.type = value.startsWith('fa-') ? 'fa' : 'mi';
  }
/**
 * <p>Icon size of menu trigger</p>
 * @param {?} value
 * @return {?}
 */
set iconSize(value: string) {
    if (this.properties.triggerIcon == null) { this.properties.triggerIcon = new IconModel(); }
    this.properties.triggerIcon.size = value;
  }
/**
 * <p>Items source type. For current time only one type exists: <b>fromModel</b></p>
 * @param {?} value
 * @return {?}
 */
set itemsSource(value: string) {
    this.properties.itemsSource = value;
  }
/**
 * <p>Menu items array</p>
 * @param {?} value
 * @return {?}
 */
set menuItems(value: Object[]) {
    this.properties.menuItems = value;
  }
/**
 * <p>Emit event on menu item click</p>
 * <p>Event has object with structure:</p>
 * <pre>{{'{'}}
 * 	 cfIndex: index, // index of menu item clicked
 * 	 cfItem: item // menu of menu item clicked
 * {{'}'}}
 * </pre>
 */
public onItemClick: EventEmitter<any> = new EventEmitter<any>();
/**
 * @hidden 
 * It is Menu trigger element (icon) in the DOM which give access to
 * open menu programmatically from other GCs Components (like GC Button) by calling function <b>openMenu()</b>.
 */
trigger: MdMenuTrigger;
/**
 * @hidden
 */
itemsTemplate;
/**
 * @hidden 
 * List of children menu items from html view
 */
contentItems: QueryList<CfMenuItemComponent>;
/**
 * @hidden
 * @param {?} i
 * @param {?} it
 * @return {?}
 */
itemClicked(i: number, it: Object) { this.onItemClick.emit({ cfIndex: i, cfItem: it }); }
/**
 * @hidden 
 * It is function which is called after click on Menu icon (trigger)
 * from other GCs Components (like GC Button) to open list with menu items.
 * @return {?}
 */
openMenu() { this.trigger.openMenu(); }
/**
 * @hidden 
 * Function to add classes to menu dropdownPanel on open
 * @return {?}
 */
onOnen() {
    if (!!this.styling && this.styling.dropdownPanel) {
      let /** @type {?} */ panelClassesStr = this.styling.dropdownPanel.class, /** @type {?} */
        panel = document.getElementsByClassName('mat-menu-panel');
      if (this.styling.dropdownPanel.dynamicClass !== '') {
        panelClassesStr = panelClassesStr;
        // + this.getElementClass('dropdownPanel');
      }
      let /** @type {?} */ panelClassesArr = panelClassesStr.split(' ');
      panelClassesArr.forEach(c => this._renderer.setElementClass(panel[0], c, true));
    }
  }
/**
 * @hidden 
 * It is function for creating Notification click events by using Core Component <b>notificationAction</b> event emmiter.
 * @param {?} notification 	it is json notification object
 * @return {?}
 */
cfNotificationAction(notification) {
    this.notificationAction.emit(notification);
  }
/**
 * @hidden
 * @param {?} _renderer
 * @param {?} elementRef
 * @param {?} templateService
 */
constructor(private _renderer: Renderer,
public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new MenuModel();
    this.styling = new MenuStylingModel();
  }
/**
 * @hidden 
 * <p>On Menu component initialization next things are heppening:</p>
 * <ul>
 * <li>menu array passed via <b>[menu]</b> attribute of <b>cf-menu</b>
 * directive is assigned to <b>cfMenu</b> array which is used inside Menu Component template
 * <li>on each <b>ngFor</b>
 * iteration menu item is generated with MenuItemModel Class
 * (inside which also Icon Class can be called to generate menu item icon)
 * </ul>
 * @return {?}
 */
ngOnInit(): void {
    if (this.notificationAction.observers.length > 0) { this.notificationClickable = 'notification-clickable'; }
    this.getMyTemplate('menu', MenuTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'MENU').then( sampler => {
        // console.log('MENU', this);
      });
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-menu',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-menu-container'\"> <cf-icon class=\"cf-menu-trigger\" [properties]=\"properties?.triggerIcon\" [styling]=\"styling?.iconStyling\" [mdMenuTriggerFor]=\"menu\" (onMenuOpen)=\"onOnen($event)\"></cf-icon> <md-menu #menu=\"mdMenu\"> <div *ngIf=\"properties.itemsSource === 'fromModel' && contentItems.length === 0\"> <cf-menu-item *ngFor=\"let menuItem of properties?.menuItems, let i = index\" [properties]=\"menuItem\" (onClick)=\"itemClicked(i, menuItem)\" [styling]=\"styling?.menuItemStyling\"> <span [ngTemplateOutlet]=\"itemsTemplate\" [ngOutletContext]=\"{cfIndex: i, cfItem: menuItem}\"></span> </cf-menu-item> </div> <div *ngIf=\"contentItems.length > 0\"><ng-content></ng-content></div> </md-menu> <cf-notification *ngIf=\"notification && (notification?.value || notification?.icon)\" [settings]=\"notification\" (click)=\"cfNotificationAction(notification)\"></cf-notification> </div>",
  styles: [":host { display: block; position: relative; z-index: 0; } :host.cf-menu-is-disabled:before { content: ''; position: absolute; z-index: 1; display: block; width: 100%; height: 100%; } :host.notification-clickable cf-notification:hover { cursor: pointer; } .cf-menu { display: flex; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'notificationClickable': [{ type: HostBinding, args: ['class', ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'iconName': [{ type: Input, args: ['iconName', ] },],
'iconSize': [{ type: Input, args: ['iconSize', ] },],
'itemsSource': [{ type: Input, args: ['itemsSource', ] },],
'menuItems': [{ type: Input, args: ['menuItems', ] },],
'onItemClick': [{ type: Output },],
'trigger': [{ type: ViewChild, args: [MdMenuTrigger, ] },],
'itemsTemplate': [{ type: ContentChild, args: ['itemsTemplate', ] },],
'contentItems': [{ type: ContentChildren, args: [CfMenuItemComponent, ] },],
};
}

function CfMenuComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfMenuComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfMenuComponent.ctorParameters;
/** @type {?} */
CfMenuComponent.propDecorators;
/**
 * @hidden
 * @type {?}
 */
CfMenuComponent.prototype.notificationClickable;
/**
 * <pre>{{'{'}}
 *  display: boolean,							// True or False <b>Default: True</b>
 *  disable: boolean,							// True or False <b>Default: False</b>
 *  triggerIcon: IconModel,				// Refer to icon component
 *  menuItems: any,								// Array of menu items with type any so each item can
 *                                  have any structure together with MenuItemModel properties
 *  itemsSource: string,				  // Type of items source. For current time exists only: <b>fromModel</b>
 * {{'}'}}
 * </pre>
 * <p>Example</p>
 * <pre>{{'{'}}
 * 	display: true,
 * 	disable: false,
 * 	itemsSource: 'fromModel',
 *  menuItems:[
 *   {{'{'}}
 *    buttonProperty:{{'{'}}
 *     label: "USD account",
 *     iconProperty:{{'{'}}
 *      name: 'fa-usd',
 *      size: '24px'
 *     {{'}'}},
 *     iconPosition:"left"
 *    {{'}'}}
 *   {{'}'}},
 *   {{'{'}}
 *    buttonProperty:{{'{'}}
 *     label: "EURO account",
 *     iconProperty:{{'{'}}
 *      name: 'fa-eur',
 *      size: '24px'
 *     {{'}'}},
 *     iconPosition:"left"
 *    {{'}'}}
 * 	 {{'}'}}
 *  ]
 *  triggerIcon:{{'{'}}
 *   name: "account_balance",
 *   size: "30px"
 *  {{'}'}}
 * {{'}'}}
 * </pre>
 * @type {?}
 */
CfMenuComponent.prototype.properties;
/**
 * <p> dynamicClass: function() -> string,	// Function that returns name of the class </p>
 * <p> class: string												// Name of the css class selector </p>
 * <p> themeColor: string										// primary/accent/warn </p>
 * <pre>
 * {{'{'}}
 * 	// Container surrounding the Menu
 * 	container:{{'{'}}
 *   dynamicClass,
 *   class
 * 	{{'}'}},
 * 	// Check Icon Component
 * 	iconStyling:IconStylingModel,
 * 	// Toggle Icon styling with same structure as above
 * 	menuItemStyling:{{'{'}}
 *   container: {{'{'}}{{'}'}},
 *   //Check Button Component
 *   buttonStyling:ButtonStylingModel
 * {{'}'}}
 * {{'}'}}
 * </pre>
 * @type {?}
 */
CfMenuComponent.prototype.styling;
/**
 * <p>Emit event on menu item click</p>
 * <p>Event has object with structure:</p>
 * <pre>{{'{'}}
 * 	 cfIndex: index, // index of menu item clicked
 * 	 cfItem: item // menu of menu item clicked
 * {{'}'}}
 * </pre>
 * @type {?}
 */
CfMenuComponent.prototype.onItemClick;
/**
 * @hidden 
 * It is Menu trigger element (icon) in the DOM which give access to
 * open menu programmatically from other GCs Components (like GC Button) by calling function <b>openMenu()</b>.
 * @type {?}
 */
CfMenuComponent.prototype.trigger;
/**
 * @hidden
 * @type {?}
 */
CfMenuComponent.prototype.itemsTemplate;
/**
 * @hidden 
 * List of children menu items from html view
 * @type {?}
 */
CfMenuComponent.prototype.contentItems;
/** @type {?} */
CfMenuComponent.prototype._renderer;
/**
 * @hidden
 * @type {?}
 */
CfMenuComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
